<template>
  <div class="app-container">
    <!-- 登录页直接显示，不显示其他组件 -->
    <router-view v-if="isLoginPage"></router-view>
    
    <!-- 非登录页显示完整布局 -->
    <div v-else>
      <!-- 顶部栏 -->
      <HeaderComponent 
        :is-desktop="isDesktop" 
        @toggle-menu="toggleMenu"
      />
      
      <!-- 主体内容区 -->
      <div class="main-wrapper">
        <!-- 左侧菜单 -->
        <MenuComponent 
          :is-desktop="isDesktop" 
          :is-menu-open="isMenuOpen"
          @menu-click="handleMenuClick"
        />
        
        <!-- 右侧主内容 -->
        <main class="app-main" :class="{ 'main-collapsed': !isMenuOpen && !isDesktop }">
          <!-- 电脑端Tab标签 -->
          <div class="tab-container" v-if="isDesktop">
            <div class="tab-bar">
              <div 
                v-for="tab in tabs" 
                :key="tab.path"
                class="tab-item"
                :class="{ 'active': activeTab === tab.path }"
                @click="switchTab(tab)"
              >
                <span class="tab-title">{{ tab.name }}</span>
                <span 
                  class="tab-close"
                  @click.stop="closeTab(tab)"
                >×</span>
              </div>
            </div>
            <div class="tab-content">
              <router-view></router-view>
            </div>
          </div>
          <!-- 手机端直接显示内容 -->
          <div v-else>
            <router-view></router-view>
          </div>
          <!-- 底部信息 -->
          <FooterComponent />
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderComponent from './components/HeaderComponent.vue'
import MenuComponent from './components/MenuComponent.vue'
import FooterComponent from './components/FooterComponent.vue'

export default {
  name: 'App',
  components: {
    HeaderComponent,
    MenuComponent,
    FooterComponent
  },
  data() {
    return {
      isDesktop: true,
      isMenuOpen: true,
      // Tab相关数据
      tabs: [],
      activeTab: '',
      // 路由信息映射（扩展为包含所有主要路由）
      routeMap: {
        
      }
    }
  },
  computed: {
    // 判断是否为登录页
    isLoginPage() {
      return this.$route.path === '/login'
    }
  },
  mounted() {
    //routeMap 初始化（重定向路由没有 name，跳过不收）
    this.routeMap = this.$router.options.routes.reduce((acc, route) => {
      if (route.name) acc[route.path] = { name: route.name }
      return acc
    }, {})
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    // 初始化默认tab（非登录页）
    if (this.$route.path !== '/login') {
      this.addTab(this.$route)
    }
    // 已登录则建立实时通道（刷新页面后同样能收到新公告推送）
    if (localStorage.getItem('token')) {
      this.$store.dispatch('connectMessageStream')
    }
  },
  watch: {
    // 监听路由变化，添加新tab
    '$route': {
      handler(to, from) {
        // 登录页不添加tab
        if (to.path !== '/login') {
          this.addTab(to)
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
    // 关闭实时通道，避免连接泄漏
    this.$store.dispatch('closeMessageStream')
  },
  methods: {
    checkScreenSize() {
      const width = window.innerWidth
      this.isDesktop = width > 768
      // 电脑端默认展开菜单，手机端默认收起菜单
      if (this.isDesktop) {
        this.isMenuOpen = true
      } else {
        this.isMenuOpen = false
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    // 添加tab
    addTab(route) {
      const path = route.path
      // 取名：优先路由自身 name（vue-router 解析完成后必有），
      // 其次查路由表。两者都没有说明是尚未解析完的初始路由（/）或
      // 重定向路由——这种情况不建 tab，等路由解析到具体页面后 watcher 会再进
      let name = route.name
      if (!name && this.routeMap[path]) {
        name = this.routeMap[path].name
      }
      if (!name) {
        const matchedRoute = this.$router.options.routes.find(r => r.path === path)
        if (matchedRoute && matchedRoute.name) {
          name = matchedRoute.name
        }
      }
      if (!name) return
      
      // 检查tab是否已存在
      const existingTab = this.tabs.find(tab => tab.path === path)
      if (!existingTab) {
        this.tabs.push({
          path: path,
          name: name
        })
      }
      // 设置当前激活的tab
      this.activeTab = path
    },
    // 切换tab
    switchTab(tab) {
      this.activeTab = tab.path
      this.$router.push(tab.path)
    },
    // 关闭tab
    closeTab(tab) {
      // 不允许关闭最后一个tab
      if (this.tabs.length <= 1) return
      
      const index = this.tabs.indexOf(tab)
      this.tabs.splice(index, 1)
      
      // 如果关闭的是当前激活的tab，切换到前一个或后一个tab
      if (this.activeTab === tab.path) {
        const newActiveIndex = index > 0 ? index - 1 : 0
        const newActiveTab = this.tabs[newActiveIndex]
        this.switchTab(newActiveTab)
      }
    },
    // 处理菜单点击事件，在手机端点击菜单后关闭菜单
    handleMenuClick() {
      this.isMenuOpen = false
    }
  }
}
</script>

<style scoped>
/* 使用全局样式中定义的 CSS 变量 */
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background);
}

/* 主体内容区布局 */
.main-wrapper {
  display: flex;
  flex: 1;
  margin-top: 80px; /* 为顶部栏留出空间 */
  height: calc(100vh - 80px); /* 减去顶部栏和底部栏的高度，使用固定高度而非min-height */
  overflow: hidden; /* 隐藏主内容区的滚动条 */
}

/* 主内容区样式 */
.app-main {
  flex: 1;
  padding: 0;
  background-color: var(--bg-200);
  transition: margin-left 0.3s ease;
}

/* Tab标签样式 */
.tab-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

/* Tab栏样式 */
.tab-bar {
  display: flex;
  background-color: var(--light);
  border-bottom: 1px solid var(--border);
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
}

/* Webkit滚动条样式 */
.tab-bar::-webkit-scrollbar {
  height: 4px;
}

.tab-bar::-webkit-scrollbar-track {
  background: transparent;
}

.tab-bar::-webkit-scrollbar-thumb {
  background-color: var(--border);
  border-radius: 2px;
}

.tab-bar::-webkit-scrollbar-thumb:hover {
  background-color: var(--border-light);
}

/* Tab项样式 */
.tab-item {
  display: flex;
  align-items: center;
  padding: 8px 20px;
  cursor: pointer;
  border-right: 1px solid var(--border);
  transition: all 0.3s ease;
  position: relative;
}

.tab-item:hover {
  background-color: var(--border-light);
}

/* 激活的Tab项样式 */
.tab-item.active {
  background-color: var(--bg-200);
  color: var(--primary);
  border-bottom: 2px solid var(--primary);
}

/* Tab标题样式 */
.tab-title {
  margin-right: 8px;
  font-size: var(--font-size-sm);
  font-weight: 500;
}

/* Tab关闭按钮样式 */
.tab-close {
  font-size: var(--font-size-xs);
  color: var(--text-200);
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.tab-close:hover {
  opacity: 1;
  color: var(--danger);
}

/* Tab内容区样式 */
.tab-content {
  flex: 1;
  padding: 15px;
  padding-bottom: 0;
  overflow: hidden;   /* 不让最外层出现滚动条，交给页面内部滚动 */
  background-color: var(--bg-200);
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 电脑端样式 */
@media (min-width: 769px) {
  .app-main {
    margin-left: 0;
    overflow: hidden; /* 电脑端隐藏主内容区滚动条 */
    padding: 10px;
    box-sizing: border-box;
    background-color: var(--background);
    border-radius: 20px;
  }

  .tab-container{
    height: calc(100% - 42px);
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
  
  .main-collapsed {
    margin-left: 0;
  }
}

/* 手机端样式 */
@media (max-width: 768px) {
  .app-main {
    position: absolute;
    top: 80px;
    height: calc(100% - 80px);
    left: 0;
    right: 0;
    bottom: 0;
    transform: translateX(240px); /* 与手机端菜单宽度一致 */
    transition: transform 0.3s ease;
    padding: 15px;
    padding-top: 0;
    z-index: 998;
    overflow-y: auto; /* 手机端允许垂直滚动 */
  }
  
  .main-collapsed {
    transform: translateX(0);
  }
}
</style>